import { FC } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Box,
} from '@mui/material'
import { Visibility as ViewIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { orderBy } from 'lodash-es'
import type { ProjectModel } from '@itemize/server/models'

export interface ProjectsTableProps {
  projects: ProjectModel[]
  onView: (project: ProjectModel) => void
  onEdit: (project: ProjectModel) => void
  onDelete: (project: ProjectModel) => void
}

export const ProjectsTable: FC<ProjectsTableProps> = ({ projects, onView, onEdit, onDelete }) => {
  const sortedProjects = orderBy(
    projects,
    [
      // First sort by status (open first)
      (project) => project.status === 'closed',
      // Then sort by updated date (most recent first)
      'updatedAt',
    ],
    ['asc', 'desc'],
  )

  return (
    <TableContainer component={Paper} elevation={2}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Project Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Created</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedProjects.map((project) => (
            <TableRow
              key={project.id}
              sx={{
                opacity: project.status === 'closed' ? 0.6 : 1,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight="medium">
                    {project.name}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {project.description || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={project.status === 'open' ? 'Open' : 'Closed'}
                  size="small"
                  variant="outlined"
                  color={project.status === 'open' ? 'success' : 'secondary'}
                />
              </TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  {new Date(project.createdAt).toLocaleDateString()}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Box display="flex" gap={0.5} justifyContent="flex-end">
                  <Tooltip title="View">
                    <IconButton onClick={() => onView(project)} size="small">
                      <ViewIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton onClick={() => onEdit(project)} size="small">
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton onClick={() => onDelete(project)} size="small">
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
